import { Box, Slider, Typography } from '@mui/material';
import { useRecoilState } from 'recoil';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import { BrushTool } from '~/engine/tool/tools/contouring-tools/BrushTool';
import {
    brushOptionsState,
    contourAutoSuggestionEnabledState,
} from '~/state/atoms/toolsAtoms';
import { cmToMm, formatMmAsCm, mmToCm } from '~/utils/units';

export default function BrushToolOptions() {
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [autoSuggestEnabled, setAutoSuggestEnabled] = useRecoilState(
        contourAutoSuggestionEnabledState
    );

    const handleSetBrushSize = (_: Event, newValue: number | number[]) => {
        const valueCm = Array.isArray(newValue) ? newValue[0] : newValue;
        const clampedCm = Math.max(0.2, valueCm);
        const newDiameter = cmToMm(clampedCm);
        if (newDiameter > BrushTool.MAX_BRUSH_SIZE) {
            return;
        }
        setBrushOptions((options) => ({ ...options, diameter: newDiameter }));
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mx: -0.5,
            }}
        >
            <Slider
                value={mmToCm(brushOptions.diameter)}
                onChange={handleSetBrushSize}
                step={0.1}
                min={-0.4}
                max={4.0}
                size="small"
                sx={{
                    minWidth: 100,
                    height: 14,
                    '& .MuiSlider-rail': {
                        height: 14,
                        borderRadius: 100,
                        opacity: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '& .MuiSlider-track': {
                        height: 14,
                        borderRadius: 100,
                        border: 'none',
                        backgroundColor: 'primary.main',
                    },
                    '& .MuiSlider-thumb': {
                        height: 12,
                        width: 12,
                        backgroundColor: '#fff',
                        border: 'none',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
                        zIndex: 1,
                        marginLeft: '-7px',
                        '&:hover, &.Mui-active, &.Mui-focusVisible': {
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                        },
                    },
                }}
            />
            <Typography
                variant="body2"
                fontSize={11}
                color="rgba(255, 255, 255, 0.85)"
                sx={{
                    flexShrink: 0,
                    minWidth: 38,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {formatMmAsCm(brushOptions.diameter, 1)} cm
            </Typography>

            {brushOptions.erase && (
                <CompactToggle
                    labelId="adaptiveErase"
                    tooltipId="eraseSmartTooltip"
                    value={brushOptions.mode === 'smart'}
                    onChange={(adaptive) =>
                        setBrushOptions((options) => ({
                            ...options,
                            mode: adaptive ? 'smart' : 'static',
                        }))
                    }
                />
            )}
            {/*
             * Auto-suggest is meaningful for the additive brush
             * variants (static / adaptive draw), where the user
             * is laying down new contour content that the system
             * can extend onto neighbouring slices. It does not
             * apply to the erase variant, so the toggle is
             * suppressed when erase mode is active.
             */}
            {!brushOptions.erase && (
                <CompactToggle
                    labelId="sliceDeformSuggest"
                    tooltipId="sliceDeformSuggestTooltip"
                    value={autoSuggestEnabled}
                    onChange={(enabled) => setAutoSuggestEnabled(enabled)}
                />
            )}
        </Box>
    );
}
